import { useTranslation } from '@/lib/i18n';
import type { Money, SettlementItem } from '@/lib/api-admin/types';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';

export interface SettlementItemsTableProps {
  items: ReadonlyArray<SettlementItem>;
}

const formatMoney = (money: Money | undefined | null): string => {
  if (!money) return '—';
  return `${new Intl.NumberFormat('ko-KR').format(money.amount)} ${money.currencyCode}`;
};

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ko-KR');
};

export const SettlementItemsTable = ({ items }: SettlementItemsTableProps) => {
  const { t } = useTranslation('settlement');
  if (items.length === 0) {
    return (
      <p
        role="status"
        aria-live="polite"
        className="rounded-md p-4 text-sm text-[color:var(--content-muted)]"
      >
        {t('settlement.items.empty')}
      </p>
    );
  }
  return (
    <div
      className="overflow-hidden rounded-lg"
      style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
    >
      <Table data-testid="settlement-items-table">
        <TableHeader style={{ background: 'var(--surface-1)' }}>
          <TableRow>
            <TableHead scope="col">{t('settlement.items.columns.source')}</TableHead>
            <TableHead scope="col">{t('settlement.items.columns.sourceLabel')}</TableHead>
            <TableHead scope="col" className="text-right">{t('settlement.items.columns.gross')}</TableHead>
            <TableHead scope="col" className="text-right">{t('settlement.items.columns.commission')}</TableHead>
            <TableHead scope="col" className="text-right">{t('settlement.items.columns.net')}</TableHead>
            <TableHead scope="col">{t('settlement.items.columns.refund')}</TableHead>
            <TableHead scope="col">{t('settlement.items.columns.memo')}</TableHead>
            <TableHead scope="col">{t('settlement.items.columns.createdAt')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow
              key={item.lineItemId}
              data-testid="settlement-item-row"
              data-source={item.sourceType}
              data-refund={item.isRefund ? 'true' : 'false'}
              style={{ height: 'var(--density-compact-row-h)' }}
            >
              <TableCell>
                <span
                  className="rounded px-1.5 py-0.5 text-[10px]"
                  style={{
                    background: 'var(--surface-1)',
                    color: 'var(--content-muted)',
                  }}
                >
                  {t(`settlement.source.${item.sourceType}`, { defaultValue: '-' })}
                </span>
              </TableCell>
              <TableCell className="text-xs text-[color:var(--content-default)]">
                {item.sourceLabel ?? item.sourceId}
              </TableCell>
              <TableCell className="text-right text-xs">
                {formatMoney(item.grossAmount)}
              </TableCell>
              <TableCell className="text-right text-xs text-[color:var(--content-muted)]">
                -{formatMoney(item.commissionAmount)}
              </TableCell>
              <TableCell className="text-right text-sm font-medium text-[color:var(--content-strong)]">
                {formatMoney(item.netAmount)}
              </TableCell>
              <TableCell className="text-xs">
                {item.isRefund ? t('settlement.items.columns.refund') : '—'}
              </TableCell>
              <TableCell className="text-xs text-[color:var(--content-muted)]">
                {item.memo ?? '—'}
              </TableCell>
              <TableCell className="text-xs text-[color:var(--content-muted)]">
                {formatDate(item.createdAt)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
